import { useNavigate, useParams } from 'react-router-dom'
import AppLayout from '~/components/appLayout'
import llmChatListDefinition from './list/llmChatList.definition'
import Sidebar from '~/components/llm/sidebar'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LlmChatProvider } from '../../components/llm/llmChatContext'
import SearchModal from '~/components/llm/searchModal'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import useAPI from '~/prix/react/hooks/api'

interface GeneralLlmChatBaseScreenProps {
  isArchived?: boolean
}

export default function GeneralLlmChatBaseScreen({
  isArchived = false,
}: GeneralLlmChatBaseScreenProps) {
  const navigate = useNavigate()
  const { chatId } = useParams()
  const api = useAPI()
  const [isSidebarOpened, setIsSidebarOpened] = useState<boolean>(true)
  const [chatList, setChatList] = useState<any[]>([])
  const [isSearchModalOpened, setIsSearchModalOpened] = useState<boolean>(false)

  const {
    callAction: reloadList,
    result: listResult,
    isLoading: loadingList,
  } = useActionFunction(llmChatListDefinition, {
    initialIsLoading: true,
  })

  useEffect(() => {
    if (chatList.length === 0) {
      setChatList(listResult?.items ?? [])
    }
  }, [listResult?.items])

  const currentChat = useMemo(() => {
    const chatIdNumber = parseInt(chatId ?? '-1')
    if (chatIdNumber === -1) return null

    return chatList.find(item => item.id === chatIdNumber)
  }, [chatId, chatList])

  const handleScrollChatListToBottom = useCallback(
    (callback: () => void) => {
      if (loadingList) return

      const actionChatListResult = reloadList({
        limit: 20,
        offset: chatList.length ?? 0,
        showArchived: isArchived ?? false,
      })

      actionChatListResult.resultPromise
        .then(result => {
          setChatList(prev => [...prev, ...(result?.items ?? [])])
        })
        .finally(() => {
          callback()
        })
    },
    [reloadList, setChatList, loadingList, isArchived, chatList],
  )

  const handleChangeListType = useCallback(
    (type: 'active' | 'archived') => {
      if (type === 'active') {
        navigate('/app/ai')
      } else {
        navigate(`/app/ai/archived`)
      }
    },
    [navigate],
  )

  const refreshChatList = useCallback(async () => {
    setChatList([])

    const actionChatListResult = reloadList({
      limit: 20,
      offset: 0,
      showArchived: isArchived ?? false,
    })

    const result = await actionChatListResult.resultPromise
    setChatList(result?.items ?? [])
  }, [reloadList, isArchived])

  useEffect(() => {
    if (api.isLoading || !api.context.user) {
      return
    }
    refreshChatList()
  }, [isArchived, api.isLoading, api.context.user, refreshChatList])

  const openSearchModal = useCallback(() => {
    setIsSearchModalOpened(true)
  }, [])

  return (
    <LlmChatProvider
      currentChatId={currentChat?.id}
      chatList={chatList}
      setChatList={setChatList}
      handleChangeListType={handleChangeListType}
      refreshChatList={refreshChatList}
      isLoadingChatList={loadingList}
    >
      <AppLayout
        title='Prix AI'
        dockActive='ai'
        mustBeAuthenticated={true}
        menuDeskWidth={320}
        onlyChildren={true}
        menu={
          <Sidebar
            chats={chatList}
            currentChatId={currentChat?.id}
            loading={loadingList}
            listType={isArchived ? 'archived' : 'active'}
            onScrollChatListToBottom={handleScrollChatListToBottom}
            hasMoreChats={listResult?.hasMore}
            onChangeListType={handleChangeListType}
            openSearchModal={openSearchModal}
          />
        }
      />

      <SearchModal
        paddingLeft={320}
        isOpen={isSearchModalOpened}
        onClose={() => setIsSearchModalOpened(false)}
        isArchived={isArchived}
      />
    </LlmChatProvider>
  )
}
